<script setup>
// A caixa de busca do agente. Ela não busca conteúdo — busca CONFIGURAÇÃO: onde fica a
// coisa que a pessoa quer mexer. Ver `busca.js` para por que o índice é declarado.
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { procurar } from './busca';

const props = defineProps({
  // Ids das seções que este papel vê; `null` não filtra.
  secoes: { type: Array, default: null },
});
const emit = defineEmits(['ir']);
const { t } = useI18n();

const consulta = ref('');
const aberto = ref(false);
const destacado = ref(0);
const campo = ref(null);

// Só as seções que este papel vê. Sem o filtro, a busca levava o cliente a uma seção de
// plataforma, que abre vazia para ele.
const resultados = computed(() =>
  procurar(consulta.value, t).filter(
    r => !props.secoes || props.secoes.includes(r.section)
  )
);

function abrir() {
  aberto.value = true;
  destacado.value = 0;
}

function fechar() {
  // No próximo tique: fechar no `blur` imediato mataria o clique no resultado, que acontece
  // depois do blur.
  setTimeout(() => {
    aberto.value = false;
  }, 120);
}

function escolher(resultado) {
  if (!resultado) return;
  emit('ir', resultado);
  consulta.value = '';
  aberto.value = false;
  campo.value?.blur();
}

function navegar(delta) {
  if (!resultados.value.length) return;
  const n = resultados.value.length;
  destacado.value = (destacado.value + delta + n) % n;
}

// Ctrl/Cmd+K é o atalho que todo mundo já tem no dedo. Registrado pelo pai, que sabe quando
// a tela do agente está montada.
async function focar() {
  aberto.value = true;
  await nextTick();
  campo.value?.focus();
}

defineExpose({ focar });
</script>

<template>
  <div class="relative">
    <div class="relative">
      <!-- pointer-events-none: sem isto o clique na lupa não chega ao input. E o ícone
           precisa de um z acima do campo, senão o `type=search` do Chrome desenha por cima. -->
      <i
        class="absolute z-10 text-sm -translate-y-1/2 pointer-events-none i-lucide-search left-2.5 top-1/2 text-n-slate-10"
      />
      <input
        ref="campo"
        v-model="consulta"
        type="search"
        class="reset-base w-56 py-1.5 pl-9 pr-9 text-sm border rounded-lg outline-none border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:border-n-brand placeholder:text-n-slate-10 [&::-webkit-search-cancel-button]:hidden"
        :placeholder="t('POWER_AGENTS.SEARCH.PLACEHOLDER')"
        @focus="abrir"
        @blur="fechar"
        @keydown.down.prevent="navegar(1)"
        @keydown.up.prevent="navegar(-1)"
        @keydown.enter.prevent="escolher(resultados[destacado])"
        @keydown.esc="aberto = false"
      />
      <!-- O atalho sai do placeholder e vira etiqueta: dentro do texto ele competia com a
           própria frase e era a metade que sobrava quando o campo encolhia. -->
      <kbd
        class="absolute px-1.5 py-0.5 text-[10px] font-medium -translate-y-1/2 border rounded pointer-events-none right-2 top-1/2 border-n-weak text-n-slate-10"
      >
        {{ t('POWER_AGENTS.SEARCH.SHORTCUT') }}
      </kbd>
    </div>

    <div
      v-if="aberto && consulta.trim().length >= 2"
      class="absolute right-0 z-30 mt-1 overflow-hidden border shadow-lg w-80 rounded-xl border-n-weak bg-n-background"
    >
      <p v-if="!resultados.length" class="px-3 py-3 text-xs text-n-slate-10">
        {{ t('POWER_AGENTS.SEARCH.EMPTY', { termo: consulta }) }}
      </p>
      <button
        v-for="(resultado, i) in resultados"
        :key="resultado.id"
        class="flex items-center w-full gap-2 px-3 py-2 text-left transition-colors"
        :class="i === destacado ? 'bg-n-alpha-2' : 'hover:bg-n-alpha-1'"
        @mouseenter="destacado = i"
        @mousedown.prevent="escolher(resultado)"
      >
        <span class="flex-1 min-w-0">
          <span class="block text-sm truncate text-n-slate-12">{{
            resultado.rotulo
          }}</span>
          <span class="block text-xs truncate text-n-slate-10">
            {{ resultado.secaoRotulo }}
          </span>
        </span>
        <i class="text-sm i-lucide-corner-down-left text-n-slate-10 shrink-0" />
      </button>
    </div>
  </div>
</template>
